import { ActionContext, ActionTree, GetterTree, Module, MutationTree, Store } from 'vuex'
import { GraphqlError, GraphqlQueryResponse } from '~/types/apolloTypes'

import { RootState } from '~/store'
import { Analyzer, AnalyzerConnection, PageInfo, TransformerTool } from '~/types/types'

import AnalyzersGQLQuery from '~/apollo/queries/analyzer/list.gql'
import AnalyzerNamesQuery from '~/apollo/queries/analyzer/names.gql'
import { resolveNodes } from '~/utils/array'

export interface AnalyzerMetaProperitiesItemInterface {
  enum: string[]
  type: string
}

export interface AnalyzerMetaProperitiesInterface {
  description: string
  name: string
  items?: AnalyzerMetaProperitiesItemInterface
  placeholder: string
  title: string
  type: string
  enum: string[]

  labels: Record<string, string>
  selected: string | number | boolean | string[] | number[]
  default: string | number | boolean | string[] | number[]
}

export interface AnalyzerMetaInterface {
  additionalProperties: boolean
  optional_required: Array<string>
  required: Array<string>
  properties: Record<string, AnalyzerMetaProperitiesInterface>
}

export interface TransformerInterface extends TransformerTool {
  name: string
  shortcode: string
  label?: string
  analyzerLogo?: string
  enabled: boolean
}

export interface AnalyzerInterface extends Analyzer {
  label: string
  icon: string
  enabled: boolean
  transformers: Array<TransformerTool>
  meta: AnalyzerMetaInterface
  isBeta?: boolean
}

export enum AnalyzerListActions {
  FETCH_ANALYZER_INFO = 'fetchAnalyzerInfo',
  FETCH_ANALYZER_LIST = 'fetchAnalyzerList',
  FETCH_ANALYZER_NAMES = 'fetchAnalyzerNames'
}

export enum AnalyzerListGetters {
  GET_ANALYZER_INFO = 'getAnalyzerInfo',
  ANALYZERS = 'getAnalyzers',
  ANALYZERS_FROM_CONNECTION = 'getAnalyzersFromConnection'
}

export enum AnalyzerListMutations {
  SET_ERROR = 'setAnalyzerListError',
  SET_LOADING = 'setAnalyzerListLoading',
  SET_ANALYZER_INFO = 'setAnalyzerInfo',
  SET_ANALYZER_LIST = 'setAnalyzerList'
}

export interface AnalyzerListModuleState {
  loading: boolean
  error: Record<string, any>
  analyzerList: AnalyzerConnection
}

export type AnalyzerListActionContext = ActionContext<AnalyzerListModuleState, RootState>

export interface AnalyzerListModuleGetters extends GetterTree<AnalyzerListModuleState, RootState> {
  [AnalyzerListGetters.ANALYZERS](state: AnalyzerListModuleState): AnalyzerInterface[]
  [AnalyzerListGetters.ANALYZERS_FROM_CONNECTION]: () => (
    analyzerList?: AnalyzerConnection | null,
    excludeShortcodes?: string[]
  ) => AnalyzerInterface[]
}

export interface AnalyzerListModuleMutations extends MutationTree<AnalyzerListModuleState> {
  [AnalyzerListMutations.SET_LOADING](state: AnalyzerListModuleState, value: boolean): void
  [AnalyzerListMutations.SET_ERROR](state: AnalyzerListModuleState, error: GraphqlError): void
  [AnalyzerListMutations.SET_ANALYZER_LIST](
    state: AnalyzerListModuleState,
    analyzerList: AnalyzerConnection
  ): void
}

export interface AnalyzerListModuleActions extends ActionTree<AnalyzerListModuleState, RootState> {
  [AnalyzerListActions.FETCH_ANALYZER_LIST](
    this: Store<RootState>,
    { commit }: AnalyzerListActionContext,
    args?: { exclude?: string[] }
  ): Promise<void>
  [AnalyzerListActions.FETCH_ANALYZER_NAMES](
    this: Store<RootState>,
    { commit }: AnalyzerListActionContext,
    args: { categories?: string[] }
  ): Promise<Analyzer[]>
}

export const getAnalyzerListFromConnection = (
  analyzerList?: AnalyzerConnection | { edges?: Array<{ node?: Analyzer | null } | null> } | null,
  excludeShortcodes: string[] = []
): AnalyzerInterface[] => {
  const analyzers: Array<Analyzer> = []
  analyzerList?.edges?.forEach((edge) => {
    if (edge?.node) {
      if (!excludeShortcodes.includes(edge.node.shortcode)) {
        analyzers.push(edge.node)
      }
    }
  })

  return analyzers.map((node) => {
    const transformers = node.transformertoolSet.edges
      .map((edge) => edge?.node)
      .filter((transformer): transformer is TransformerTool => Boolean(transformer))
    return {
      name: node.shortcode,
      shortcode: node.shortcode,
      label: node.name,
      analyzerLogo: node.analyzerLogo,
      analyzerType: node.analyzerType,
      icon: node.shortcode,
      enabled: false,
      transformers,
      meta: JSON.parse(node.metaSchema)
    } as AnalyzerInterface
  })
}

const analyzerListModule: Module<AnalyzerListModuleState, RootState> = {
  state: (): AnalyzerListModuleState => ({
    loading: false,
    error: {},
    analyzerList: {
      pageInfo: {} as PageInfo,
      edges: []
    }
  }),

  getters: {
    [AnalyzerListGetters.ANALYZERS]: (state) => {
      return getAnalyzerListFromConnection(state.analyzerList)
    },
    [AnalyzerListGetters.ANALYZERS_FROM_CONNECTION]: () => (analyzerList, excludeShortcodes) => {
      return getAnalyzerListFromConnection(analyzerList, excludeShortcodes)
    }
  } as AnalyzerListModuleGetters,

  mutations: {
    [AnalyzerListMutations.SET_LOADING](state, value) {
      state.loading = value
    },
    [AnalyzerListMutations.SET_ERROR](state, error) {
      state.error = Object.assign({}, state.error, error)
    },
    [AnalyzerListMutations.SET_ANALYZER_LIST](state, analyzerList) {
      state.analyzerList = Object.assign({}, state.analyzerList, analyzerList)
    }
  } as AnalyzerListModuleMutations,

  actions: {
    async [AnalyzerListActions.FETCH_ANALYZER_LIST]({ commit }, { exclude } = {}) {
      commit(AnalyzerListMutations.SET_LOADING, true)
      await this.$fetchGraphqlData(AnalyzersGQLQuery, { exclude })
        .then((response: GraphqlQueryResponse) => {
          commit(AnalyzerListMutations.SET_ANALYZER_LIST, response.data.analyzers)
          commit(AnalyzerListMutations.SET_LOADING, false)
        })
        .catch((e: GraphqlError) => {
          commit(AnalyzerListMutations.SET_ERROR, e)
          commit(AnalyzerListMutations.SET_LOADING, false)
        })
    },

    async [AnalyzerListActions.FETCH_ANALYZER_NAMES](_, { categories }) {
      const response = (await this.$fetchGraphqlData(AnalyzerNamesQuery, {
        categories
      })) as GraphqlQueryResponse

      return resolveNodes(response.data.analyzers)
    }
  } as AnalyzerListModuleActions
}

export default analyzerListModule
